import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";

const getWsUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return backendUrl.replace(/^http/, "ws") + "/ws";
};

export const useWebSocket = () => {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const reconnectTimer = useRef(null);

  const connect = useCallback(() => {
    const token = localStorage.getItem("session_token");
    if (!token) return;

    const wsUrl = `${getWsUrl()}/${token}`;
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        // Start ping interval
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping");
        }, 30000);
        ws._pingInterval = pingInterval;
      };

      ws.onmessage = (event) => {
        if (event.data === "pong") return;
        try {
          const data = JSON.parse(event.data);
          setNotifications((prev) => [data, ...prev].slice(0, 50));

          // Show toast notification
          if (data.type === "new_message") {
            toast.info(`New message from ${data.sender_name}`, {
              description: data.content,
              duration: 5000,
            });
          } else if (data.type === "document_uploaded") {
            toast.info(`Document uploaded by ${data.uploader_name}`, {
              description: data.filename,
              duration: 5000,
            });
          } else if (data.type === "payment_completed") {
            toast.success(`Payment received from ${data.client_name}`, {
              description: `INR ${data.amount}`,
              duration: 5000,
            });
          }
        } catch {}
      };

      ws.onclose = () => {
        setConnected(false);
        if (ws._pingInterval) clearInterval(ws._pingInterval);
        // Reconnect after 5s
        reconnectTimer.current = setTimeout(() => {
          if (localStorage.getItem("session_token")) connect();
        }, 5000);
      };

      ws.onerror = () => {
        // Silently close - preview environments may not support WebSocket
        try { ws.close(); } catch {}
      };
    } catch {}
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    if (wsRef.current) {
      if (wsRef.current._pingInterval) clearInterval(wsRef.current._pingInterval);
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  useEffect(() => {
    return () => disconnect();
  }, [disconnect]);

  return { connect, disconnect, connected, notifications, clearNotifications };
};
